import { useEffect, useRef, useState } from 'react';
import { supabase } from '@/lib/supabase';
import { listenToTopic } from './documentChannel';
import type { SheetRow } from './types';

/**
 * What a document's rows are doing while you are looking at them.
 *
 * Rows change without this page touching them: a colleague editing, and the
 * Worker writing back a formula result or migrating a borrowed value into the
 * cells that chose it. Reloading to find out is a poor way to learn.
 *
 * A trigger broadcasts the ids of the rows a statement changed on the document's
 * private channel (`documentChannel.ts`); this reads those rows back through
 * PostgREST, so RLS decides what arrives - listening widens nothing.
 *
 * The patch is deliberately narrow: the rows that changed, replaced in place. A
 * whole-document refetch would fight with whatever the user is doing, and a
 * spinner over a grid someone is typing in is worse than a stale cell.
 */
export type LiveSheetChange =
  /** `computed` is the row's formula results, laid into `row.data`'s empty cells. */
  | { kind: 'upsert'; row: SheetRow; computed: Record<string, unknown> }
  | { kind: 'delete'; id: string }
  /** Changes may have been missed (the connection dropped); the page should offer a reload. */
  | { kind: 'stale' };

export interface LiveSheetOptions {
  /** Whether the page is showing this row; a row it does not show is not reported deleted. */
  readonly hasRow?: (id: string) => boolean;
}

/** Notices that arrive close together (a paste, the Worker's write-back) are read in one request. */
const READ_DELAY_MS = 150;
const READ_BATCH = 200;

const asObject = (value: unknown) =>
  value && typeof value === 'object' && !Array.isArray(value) ? value as Record<string, unknown> : {};

export function useLiveSheetRows(
  documentId: string | null | undefined,
  tabId: string | null | undefined,
  onChange: (change: LiveSheetChange) => void,
  options: LiveSheetOptions = {},
): void {
  // Kept in refs so a new callback identity does not tear the subscription
  // down and build it again on every render.
  const handler = useRef(onChange);
  handler.current = onChange;
  const hasRow = useRef(options.hasRow);
  hasRow.current = options.hasRow;

  useEffect(() => {
    if (!documentId || !tabId) return;
    let active = true;
    let timer: ReturnType<typeof setTimeout> | null = null;
    const pending = new Set<string>();
    const controller = new AbortController();

    const read = async () => {
      timer = null;
      const ids = [...pending];
      pending.clear();
      for (let i = 0; i < ids.length; i += READ_BATCH) {
        const part = ids.slice(i, i + READ_BATCH);
        const { data, error } = await supabase
          .from('sheet_rows')
          .select('id, tab_id, position, data, computed')
          .eq('document_id', documentId)
          .in('id', part)
          .abortSignal(controller.signal);
        if (!active) return;
        if (error) { handler.current({ kind: 'stale' }); return; }
        const found = new Set<string>();
        for (const row of data ?? []) {
          if (row.tab_id !== tabId) continue; // moved to another tab: left this one
          found.add(row.id);
          handler.current({
            kind: 'upsert',
            row: { id: row.id, position: Number(row.position ?? 0), data: mergeStoredRow(row.data, row.computed) },
            computed: asObject(row.computed),
          });
        }
        // Changed but no longer readable here (trashed, moved away, access gone).
        for (const id of part) if (!found.has(id) && hasRow.current?.(id)) handler.current({ kind: 'delete', id });
      }
    };

    const stop = listenToTopic(`sheet:${documentId}`, {
      event: 'rows',
      onMessage: (payload) => {
        if (payload.tab_id !== tabId) return;
        const ids = Array.isArray(payload.ids) ? payload.ids.filter((id): id is string => typeof id === 'string') : null;
        // Too many rows for one notice: the page reloads the tab rather than patching.
        if (!ids) { handler.current({ kind: 'stale' }); return; }
        if (payload.op === 'DELETE') {
          for (const id of ids) if (hasRow.current?.(id)) handler.current({ kind: 'delete', id });
          return;
        }
        for (const id of ids) pending.add(id);
        if (timer === null) timer = setTimeout(() => { void read(); }, READ_DELAY_MS);
      },
      onResync: () => handler.current({ kind: 'stale' }),
    });

    return () => {
      active = false;
      controller.abort();
      if (timer !== null) clearTimeout(timer);
      stop();
    };
  }, [documentId, tabId]);
}

/**
 * What the grid shows for a row: what was typed, with formula results laid over
 * it. A calculated column keeps nothing in `data`, so without `computed` a live
 * update would blank the cell the Worker just filled in.
 */
export function mergeStoredRow(data: unknown, computed: unknown): Record<string, unknown> {
  const stored = data && typeof data === 'object' && !Array.isArray(data) ? { ...data as Record<string, unknown> } : {};
  const results = computed && typeof computed === 'object' && !Array.isArray(computed) ? computed as Record<string, unknown> : {};
  // Only into cells that hold nothing. A stored column with a formula over it keeps its typed
  // value in `data`, and the module code that works columns out reads that value: laying the
  // formula's own result over it would feed the formula back into its inputs, and each save of
  // `computed` would then echo back through here and move the numbers again.
  for (const [field, value] of Object.entries(results)) {
    if (value === null || value === undefined) continue;
    const own = stored[field];
    if (own === null || own === undefined || own === '') stored[field] = value;
  }
  return stored;
}

/**
 * Bumps a counter whenever this document's borrowed lists are refreshed, so a
 * grid can re-read its options without polling for them.
 */
export function useLiveLinkedLists(documentId: string | null | undefined): number {
  const [revision, setRevision] = useState(0);

  useEffect(() => {
    if (!documentId) return;
    const bump = () => setRevision((current) => current + 1);
    return listenToTopic(`sheet:${documentId}`, { event: 'lists', onMessage: bump, onResync: bump });
  }, [documentId]);

  return revision;
}
